import React, { useContext, useEffect, useState } from "react";
import logo from "./../assets/logo.png";
import { Button } from "./ui/button";
import { Link, useLocation } from "react-router-dom";
import { VscAccount, VscMenu } from "react-icons/vsc";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Separator } from "./ui/separator";
import { Bell, Mail, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ApiContext } from "../context/ApiContext";
import { motion } from "framer-motion";

const Navbar = () => {
  const { logout, user } = useContext(ApiContext);
  const token = localStorage.getItem("token");
  const [allNotifications, setAllNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setAllNotifications(user?.notifications || []);
  }, [user?._id]);

  useEffect(() => {
    // Close mobile menu when route changes
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Conditionally define navLinks based on token
  const navLinks = [
    { path: "/", label: "Home" }, 
    { path: "/about_us", label: "About Us" },
    { path: "/contact_us", label: "Contact Us" },
    ...(token ? [{ path: "/wallet", label: "E Wallet" }] : []),
  ];

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md shadow-lg" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-shrink-0"
          >
            <Link to="/">
              <img src={logo} alt="Company Logo" className="h-14 w-auto hover:scale-105 transition-transform" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex space-x-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 group ${
                      location.pathname === link.path ? "text-primary" : "text-gray-700 hover:text-primary"
                    }`}
                  >
                    {link.label}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300 ${
                      location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                    }`}></span>
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Notifications */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Popover>
                <PopoverTrigger className="relative p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                  <Bell className="h-5 w-5 text-gray-700" />
                  {allNotifications?.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {allNotifications.length}
                    </span>
                  )}
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 shadow-xl border border-gray-200 rounded-xl overflow-hidden">
                  <div className="space-y-2">
                    <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600">
                      <h3 className="text-sm font-medium text-white">Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {allNotifications?.slice(0, 6)?.map((noti, index) => (
                        <div
                          key={index}
                          className="flex gap-3 items-start p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                        >
                          <div className="bg-blue-100 p-2 rounded-full">
                            <Mail className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{noti.title || "New Notification"}</p>
                            <p className="text-xs text-gray-500 mt-1">{noti.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{new Date(noti.createdAt).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                      {allNotifications?.length === 0 && (
                        <div className="p-4 text-center">
                          <p className="text-sm text-gray-500">No new notifications</p>
                        </div>
                      )}
                    </div>
                    <Link
                      to="/notification"
                      className="block text-center text-sm font-medium text-primary px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      View All Notifications
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            </motion.div>

            {/* User Profile */}
            {token ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="focus:outline-none">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative"
                  >
                    {user?.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt="Profile"
                        className="h-9 w-9 rounded-full object-cover border-2 border-white shadow-md hover:border-primary transition-all"
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white shadow-md">
                        <VscAccount className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-white"></span>
                  </motion.div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 shadow-xl border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600">
                    <DropdownMenuLabel className="text-white">
                      {user?.name || "My Account"}
                    </DropdownMenuLabel>
                    <p className="text-xs text-white/80">{user?.email}</p>
                  </div>
                  <div className="p-2">
                    <Link to="/profile">
                      <DropdownMenuItem className="cursor-pointer rounded-lg px-3 py-2 hover:bg-gray-50">
                        Profile
                      </DropdownMenuItem>
                    </Link>
                    <Link to="/booking_list">
                      <DropdownMenuItem className="cursor-pointer rounded-lg px-3 py-2 hover:bg-gray-50">
                        Bookings
                      </DropdownMenuItem>
                    </Link>
                    {/* <Link to="/travelhistory">
                      <DropdownMenuItem className="cursor-pointer rounded-lg px-3 py-2 hover:bg-gray-50">
                        Travel Bookings
                      </DropdownMenuItem>
                    </Link> */}
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 rounded-lg px-3 py-2"
                      onClick={logout}
                    >
                      Logout
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button className="relative h-10 px-6 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-md rounded-full overflow-hidden group">
                    <span className="relative z-10">SIGN IN</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                </motion.div>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            {token && (
              <motion.div
                whileTap={{ scale: 0.9 }}
              >
                <Popover>
                  <PopoverTrigger className="relative p-2 rounded-full bg-gray-100">
                    <Bell className="h-5 w-5 text-gray-700" />
                    {allNotifications?.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {allNotifications.length}
                      </span>
                    )}
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0 shadow-xl border border-gray-200 rounded-xl">
                    <div className="space-y-2">
                      <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600">
                        <h3 className="text-sm font-medium text-white">Notifications</h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {allNotifications?.slice(0, 6)?.map((noti, index) => (
                          <div
                            key={index}
                            className="flex gap-3 items-start p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          >
                            <div className="bg-blue-100 p-1.5 rounded-full">
                              <Mail className="h-4 w-4 text-blue-600" />
                            </div>
                            <p className="text-xs text-gray-600">{noti.message}</p>
                          </div>
                        ))}
                        {allNotifications?.length === 0 && (
                          <p className="text-xs text-gray-500 px-2 py-3 text-center">
                            No new notifications
                          </p>
                        )}
                      </div>
                      <Link
                        to="/notification"
                        className="block text-center text-xs font-medium text-primary px-2 py-2 hover:bg-gray-50"
                      >
                        View All Notifications
                      </Link>
                    </div>
                  </PopoverContent>
                </Popover>
              </motion.div>
            )}
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 rounded-full bg-gray-100 text-gray-700 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <VscMenu className="h-5 w-5" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{
          height: mobileMenuOpen ? "auto" : 0,
          opacity: mobileMenuOpen ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden bg-white shadow-lg overflow-hidden"
      >
        <div className="px-4 pt-2 pb-6 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === link.path
                  ? "bg-blue-50 text-primary"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {token ? (
            <>
              <Link
                to="/profile"
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === "/profile"
                    ? "bg-blue-50 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Profile
              </Link>
              <Link
                to="/booking_list"
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === "/booking_list"
                    ? "bg-blue-50 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Bookings
              </Link>
              {/* <Link
                to="/travelhistory"
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === "/travelhistory"
                    ? "bg-blue-50 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Travel Bookings
              </Link> */}
              <button
                onClick={logout}
                className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="block text-center px-4 py-3 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              SIGN IN
            </Link>
          )}
        </div>
      </motion.div>
    </header>
  );
};

export default Navbar;